<template>
  <card v-if="user" :title="$t('pages.profile.sections.password.title')">
    <form @submit.prevent="changePassword">
      <TextInput
        v-model="passwords.password"
        type="password"
        name="password"
        :error="errors?.password"
        :label="$t('user.password')"
        :placeholder="$t('pages.profile.sections.password.passwordPlaceholder')"
      />
      <TextInput
        v-model="passwords.passwordRepeat"
        type="password"
        name="password-repeat"
        :error="errors?.passwordRepeat"
        :label="$t('user.passwordRepeat')"
        :placeholder="$t('pages.profile.sections.password.passwordRepeatPlaceholder')"
      />
      <custom-button
        variant="secondary"
        type="submit"
        class="self-start mt-3"
        :busy="loading"
      >
        {{ $t("pages.profile.sections.password.changePasswordButtonText") }}
      </custom-button>
    </form>
  </card>
</template>

<script lang="ts" setup>
import Card from '@components/utils/Card.vue';
import TextInput from '@components/utils/inputs/Input.vue';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { object, string, ref as yupRef } from 'yup';
import { useStore } from '@/store';
import { useFetchAction } from '@/hooks/fetch';
import type { User } from '@/entities/Entities';
import { useValidation } from '@/hooks/form';
import { useNotify } from '@/components/utils/notifications';

const store = useStore();
const user = ref({ ...store.state.auth.user });
const { t } = useI18n();

const schema = object({
  password: string().required(t('pages.profile.sections.password.passwordRequired')),
  passwordRepeat: string().oneOf(
    [yupRef('password'), null],
    t('pages.profile.sections.password.passwordMismatch'),
  ),
});

const {
  data: passwords,
  errors,
  validatedFn,
} = useValidation(
  schema,
  reactive({
    password: '',
    passwordRepeat: '',
  }),
);

const [exec, { loading }] = useFetchAction();

const notify = useNotify();

const changePassword = validatedFn(async () => {
  try {
    await exec<User>('/users/set-password', {
      method: 'PUT',
      body: {
        password: passwords.value.password,
      },
    });
    notify?.({
      variant: 'success',
      title: t('pages.profile.sections.password.saveSuccess'),
    });
    store.dispatch('auth/LOAD_USER');
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.profile.sections.password.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
form {
  @apply flex flex-col gap-y-5;
}
</style>
